'use client';

import { useEffect, useState } from 'react';
import { Car, History, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useVehicleHistoryStore } from '@/stores/vehicle-history';
import { useCartStore } from '@/stores/cart';
import { useVehicleStore } from '@/stores/vehicle';

interface Props {
  /** Tone for the parent surface — chips adapt colors for contrast. */
  variant?: 'light' | 'dark';
  /** Max chips to render. */
  limit?: number;
  /** Behaviour on click: 'set' = activate vehicle without navigation, 'navigate' = also go to /fordon/[ktype]. */
  action?: 'set' | 'navigate';
}

/**
 * Recent vehicles strip — one-click to re-activate a previously searched
 * registration. Reads from the persisted history store, so it follows the user
 * across the site without an API roundtrip.
 */
export function RecentVehiclesChips({ variant = 'dark', limit = 4, action = 'set' }: Props) {
  const router = useRouter();
  const entries = useVehicleHistoryStore((s) => s.entries);
  const removeEntry = useVehicleHistoryStore((s) => s.removeEntry);
  const setVehicle = useCartStore((s) => s.setVehicle);
  const setCurrentVehicle = useVehicleStore((s) => s.setVehicle);
  const selectedKtype = useCartStore((s) => s.selectedVehicle?.ktype);

  // Hydration guard — persisted store is empty until mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted || entries.length === 0) return null;

  const filtered = entries.filter((e) => e.ktype !== selectedKtype).slice(0, limit);
  if (filtered.length === 0) return null;

  const handlePick = (entry: typeof entries[number]) => {
    setVehicle(entry.vehicle, entry.plate, 'senaste-bilar-chip');
    setCurrentVehicle(entry.vehicle, entry.plate);
    if (action === 'navigate') router.push(`/fordon/${entry.ktype}?plate=${entry.plate}`);
  };

  const headerColor = variant === 'dark' ? 'text-white/70' : 'text-gray-600';
  const chipBg = variant === 'dark'
    ? 'bg-white/10 hover:bg-white/20 border-white/15 text-white'
    : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-800';
  const removeColor = variant === 'dark' ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-700';

  return (
    <div className="mt-4 max-w-2xl mx-auto">
      <div className={`flex items-center gap-2 text-xs ${headerColor} mb-2`}>
        <History className="h-3.5 w-3.5" />
        <span className="font-medium">Senast sökta</span>
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        {filtered.map((entry) => (
          <div
            key={entry.plate}
            className={`group inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-full border ${chipBg} text-sm`}
          >
            <button
              type="button"
              onClick={() => handlePick(entry)}
              className="inline-flex items-center gap-2"
            >
              <Car className="h-3.5 w-3.5" />
              <span className="font-mono font-semibold text-xs">{entry.plate}</span>
              <span className="text-xs opacity-80">
                {entry.vehicle.manufacturer}{' '}
                {entry.vehicle.model.replace(/\s*\(.*$/, '').slice(0, 14)}
              </span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeEntry(entry.plate);
              }}
              aria-label={`Glöm ${entry.plate}`}
              className={`shrink-0 p-0.5 rounded-full ${removeColor} opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
